// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// CSRF Token Management
let csrfToken = null;

// Fetch CSRF token
const fetchCSRFToken = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/csrf-token`, {
      credentials: 'include'
    });
    if (response.ok) {
      const data = await response.json();
      csrfToken = data.csrfToken;
      return csrfToken;
    }
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
  }
  return null;
};

// Initialize CSRF token on app load
fetchCSRFToken();

export const apiConfig = {
  baseURL: API_BASE_URL,
  endpoints: {
    auth: {
      login: `${API_BASE_URL}/auth/login`,
      register: `${API_BASE_URL}/auth/register`,
      verify: `${API_BASE_URL}/auth/verify`,
      profile: `${API_BASE_URL}/auth/profile`
    },
    classes: {
      create: `${API_BASE_URL}/classes`,
      list: `${API_BASE_URL}/classes`,
      join: `${API_BASE_URL}/classes/join`,
      details: (classId) => `${API_BASE_URL}/classes/${classId}`,
      update: (classId) => `${API_BASE_URL}/classes/${classId}`,
      removeStudent: (classId, studentId) => `${API_BASE_URL}/classes/${classId}/students/${studentId}`
    },
    progress: {
      game: `${API_BASE_URL}/progress/game`,
      dashboard: `${API_BASE_URL}/progress/dashboard`
    }
  }
};

// Helper function for API calls
export const apiCall = async (url, options = {}) => {
  // Ensure URL is absolute
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  
  // Check if this is a state-changing request that needs CSRF token
  const needsCSRF = options.method && ['POST', 'PUT', 'DELETE'].includes(options.method.toUpperCase());
  
  // If we need CSRF token but don't have one, fetch it
  if (needsCSRF && !csrfToken) {
    await fetchCSRFToken();
  }
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(needsCSRF && csrfToken && { 'X-CSRF-Token': csrfToken })
    },
    credentials: 'include' // Include cookies for authentication
  };

  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers
    }
  };

  // Only log in development mode for security
  if (process.env.NODE_ENV === 'development') {
    console.log('API Call:', fullUrl);
  }
  
  const response = await fetch(fullUrl, finalOptions);
  
  if (process.env.NODE_ENV === 'development') {
    console.log('API Response:', response.status, response.statusText);
  }
  
  // Handle CSRF token errors
  if (response.status === 403 && needsCSRF) {
    // CSRF token might be invalid, try to refresh it
    csrfToken = null; // Clear the old token
    await fetchCSRFToken();
    if (csrfToken) {
      // Retry the request with new token
      finalOptions.headers['X-CSRF-Token'] = csrfToken;
      return fetch(fullUrl, finalOptions);
    }
  }
  
  // Handle 401 errors - cookies are managed by the server
  // Don't redirect here, let the component handle it
  
  return response;
};

export default apiConfig;
